import { useState } from 'react';
import type { View } from '../../types';
import { ArticleEditor } from '../ui/ArticleEditor';
import { ProgrammeFormSchema, type ProgrammeFormData } from '../../lib/api/types';
import { useProgramme, useCreateProgramme, useUpdateProgramme } from '../../lib/api/hooks/programme';
import { showToast } from '../../lib/toast';

interface ProgrammeEditorProps {
  programmeId?: string;
  setView: (view: View) => void;
}

const ProgrammeEditor: React.FC<ProgrammeEditorProps> = ({ programmeId, setView }) => {
  const [isSaving, setIsSaving] = useState(false);

  
  const { data: programmeData, isLoading } = useProgramme(programmeId || '');

  // Mutations
  const { mutate: createProgramme } = useCreateProgramme();
  const { mutate: updateProgramme } = useUpdateProgramme();

  // Highlight options specific to Programme
  const highlightOptions = [
    { key: 'landingPageAfsaProgramme', label: 'Landing page/Hero section' },
    { key: 'allowComments', label: 'Allow Comments', defaultValue: true }
  ];

  const handleSubmit = (data: ProgrammeFormData, status: 'DRAFT' | 'PUBLISH') => {
    setIsSaving(true);

    // Convert comma-separated articleTag string to array
    const articleTagArray = data.articleTag
      ? data.articleTag.split(',').map((tag) => tag.trim()).filter(Boolean)
      : [];

    const payload = {
      title: data.title,
      coverImageTitle: data.coverImageTitle,
      articleTag: articleTagArray,
      landingPageAfsaProgramme: data.landingPageAfsaProgramme,
      allowComments: data.allowComments,
      content: data.content,
      status,
      coverImageUrl: data.coverImage // Form uses 'coverImage' but API expects 'coverImageUrl'
    };

    if (programmeId) {
      // Update existing programme
      updateProgramme(
        { programmeId, data: payload },
        {
          onSuccess: (response) => {
            if (response.success) {
              showToast.success('Programme updated successfully!');
              setView({ page: 'Programmes' });
            }
          },
          onError: (error: Error) => {
            showToast.error('Failed to update programme: ' + (error.message || 'Unknown error'));
            setIsSaving(false);
          }
        }
      );
    } else {
      // Create new programme
      createProgramme(payload, {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success('Programme created successfully!');
            setView({ page: 'Programmes' });
          }
        },
        onError: (error: Error) => {
          showToast.error('Failed to create programme: ' + (error.message || 'Unknown error'));
          setIsSaving(false);
        }
      });
    }
  };

  return (
    <ArticleEditor
      formSchema={ProgrammeFormSchema}
      articleId={programmeId}
      articleData={programmeData?.data}
      isLoading={isLoading}
      onBack={() => setView({ page: 'Programmes' })}
      onSubmit={handleSubmit}
      highlightOptions={highlightOptions}
      isSaving={isSaving}
    />
  );
};

export default ProgrammeEditor;
